import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Upload, Camera, Video, User, Download, 
  Image as ImageIcon, Play, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MediaEventView = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploaderName, setUploaderName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [viewingMedia, setViewingMedia] = useState(null);
  const [deletingMedia, setDeletingMedia] = useState(null);

  useEffect(() => {
    loadEvent();
    // Load saved name from localStorage
    const savedName = localStorage.getItem('uploaderName');
    if (savedName) {
      setUploaderName(savedName);
    }
  }, [eventId]);

  const loadEvent = async () => {
    try {
      const response = await axios.get(`${API}/photo-video-events/${eventId}`);
      setEvent(response.data.event);
    } catch (error) {
      toast.error('Failed to load event');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Check file sizes
    const invalidFiles = files.filter(f => f.size > 10 * 1024 * 1024);
    if (invalidFiles.length > 0) {
      toast.error(`${invalidFiles.length} file(s) exceed 10MB limit`);
      return;
    }
    
    setSelectedFiles(files);
    setShowUploadModal(true);
  };

  const handleUpload = async () => {
    if (!uploaderName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    
    // Save name to localStorage
    localStorage.setItem('uploaderName', uploaderName);

    let successCount = 0;
    let failCount = 0;

    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('uploader_name', uploaderName);

        await axios.post(
          `${API}/photo-video-events/${eventId}/upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        successCount++;
      } catch (error) {
        console.error('Upload error:', error);
        failCount++;
      }
    }

    setUploading(false);
    setShowUploadModal(false);
    setSelectedFiles([]);

    if (successCount > 0) {
      toast.success(`${successCount} file(s) uploaded successfully!`);
      loadEvent(); // Reload to show new uploads
    }
    if (failCount > 0) {
      toast.error(`${failCount} file(s) failed to upload`);
    }
  };

  const handleDownloadMedia = async (mediaId, filename) => {
    try {
      const response = await fetch(`${API}/photo-video-events/${eventId}/media/${mediaId}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded!');
    } catch (error) {
      toast.error('Failed to download');
      console.error(error);
    }
  };

  const handleDeleteMedia = async (mediaId, mediaName) => {
    if (!window.confirm(`Delete "${mediaName}"?`)) return;

    setDeletingMedia(mediaId);
    try {
      await axios.delete(`${API}/photo-video-events/${eventId}/media/${mediaId}`);
      toast.success('Media deleted!');
      loadEvent(); // Reload to update the list
    } catch (error) {
      toast.error('Failed to delete');
      console.error(error);
    } finally {
      setDeletingMedia(null);
    }
  };

  const getMediaUrl = (mediaId) => {
    return `${API}/photo-video-events/${eventId}/media/${mediaId}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <p className="text-slate-300">Loading event...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Card className="p-8 bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-center">
          <Camera className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Event Not Found</h2>
          <p className="text-slate-400">This photo/video event doesn't exist or has been deleted.</p>
        </Card>
      </div>
    );
  }

  const photos = event.mediaFiles?.filter(f => f.fileType === 'image') || [];
  const videos = event.mediaFiles?.filter(f => f.fileType === 'video') || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center mx-auto mb-4">
            <Camera className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            {event.name}
          </h1>
          {event.description && (
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">{event.description}</p>
          )}
          <div className="mt-4 flex items-center justify-center gap-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700">
              <Camera className="w-4 h-4 text-pink-400" />
              <span className="text-slate-300">{photos.length} Photos</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700">
              <Video className="w-4 h-4 text-orange-400" />
              <span className="text-slate-300">{videos.length} Videos</span>
            </div>
          </div>
        </div>

        {/* Expiration Warning */}
        {event.expiresAt && (
          <Card className="p-4 bg-orange-500/20 backdrop-blur-sm border border-orange-500/50 mb-6">
            <div className="flex items-center gap-3">
              <div className="text-2xl">⏰</div>
              <div>
                <p className="text-sm font-semibold text-orange-300">Auto-Delete in 6 Days</p>
                <p className="text-xs text-slate-300">
                  This event will be automatically deleted on {new Date(event.expiresAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Created By */}
        {event.createdBy && event.createdBy.name && (
          <Card className="p-4 bg-slate-800/30 backdrop-blur-sm border border-slate-700 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                <User className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Created by</p>
                <p className="text-white font-semibold">{event.createdBy.name}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Upload Button */}
        <div className="mb-8">
          <input
            id="fileInput"
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <label htmlFor="fileInput" className="cursor-pointer block">
            <div className="w-full bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white py-6 text-lg font-semibold shadow-lg shadow-pink-500/50 rounded-lg flex items-center justify-center transition-all">
              <Upload className="w-5 h-5 mr-2" />
              Upload Photos & Videos (No Limit!)
            </div>
          </label>
          <p className="text-center text-sm text-slate-400 mt-2">
            Max 10MB per file • Supports JPG, PNG, MP4, MOV
          </p>
        </div>

        {/* Download All Button */}
        {event.mediaFiles && event.mediaFiles.length > 0 && (
          <div className="mb-8">
            <Button
              onClick={async () => {
                try {
                  toast.loading('Preparing ZIP file...');
                  const response = await axios.get(`${API}/photo-video-events/${eventId}/download-all`, {
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(response.data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${event.name.replace(/[^a-z0-9]/gi, '_')}_photos.zip`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                  toast.success('Download started!');
                } catch (error) {
                  toast.error('Failed to download all files');
                  console.error(error);
                }
              }}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white py-6 text-lg font-semibold shadow-lg shadow-blue-500/50 rounded-lg flex items-center justify-center transition-all"
            >
              <Download className="w-5 h-5 mr-2" />
              Download All as ZIP ({event.mediaFiles.length} files)
            </Button>
          </div>
        )}

        {/* Media Grid */}
        {event.mediaFiles && event.mediaFiles.length > 0 ? (
          <div>
            {/* Photos Section */}
            {photos.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Camera className="w-6 h-6 text-pink-400" />
                  Photos ({photos.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {photos.map((media) => (
                    <div
                      key={media.id}
                      className="group relative aspect-square bg-slate-800 rounded-lg overflow-hidden"
                    >
                      <img
                        src={getMediaUrl(media.id)}
                        alt={media.originalName}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => setViewingMedia(media)}
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                        <p className="text-xs text-white font-semibold">{media.uploaderName}</p>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMedia(media.id, media.uploaderName);
                          }}
                          disabled={deletingMedia === media.id}
                          size="sm"
                          className="bg-red-500 hover:bg-red-600 text-white"
                        >
                          {deletingMedia === media.id ? 'Deleting...' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Videos Section */}
            {videos.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Video className="w-6 h-6 text-orange-400" />
                  Videos ({videos.length})
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {videos.map((media) => (
                    <Card
                      key={media.id}
                      className="p-4 bg-slate-800/50 backdrop-blur-sm border border-slate-700 hover:border-orange-500 transition-all"
                    >
                      <div className="aspect-video bg-slate-900 rounded-lg mb-3 flex items-center justify-center">
                        <video
                          src={getMediaUrl(media.id)}
                          controls
                          className="w-full h-full rounded-lg"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{media.uploaderName}</p>
                          <p className="text-xs text-slate-400">
                            {(media.fileSize / (1024 * 1024)).toFixed(1)}MB
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleDownloadMedia(media.id, media.originalName)}
                            size="sm"
                            variant="outline"
                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeleteMedia(media.id, media.uploaderName)}
                            disabled={deletingMedia === media.id}
                            size="sm"
                            className="bg-red-500 hover:bg-red-600 text-white"
                          >
                            {deletingMedia === media.id ? '...' : <X className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <Card className="p-12 bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-center">
            <Camera className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Media Yet</h3>
            <p className="text-slate-400 mb-6">Be the first to upload photos or videos!</p>
          </Card>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowUploadModal(false)}>
            <Card className="bg-slate-800 border border-slate-700 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-2xl font-bold text-white mb-4">Upload Media</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  value={uploaderName}
                  onChange={(e) => setUploaderName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              <div className="mb-6 p-4 bg-slate-700/30 rounded-lg border border-slate-600">
                <p className="text-sm text-slate-300 mb-2">Selected Files: {selectedFiles.length}</p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {selectedFiles.map((file, idx) => (
                    <p key={idx} className="text-xs text-slate-400 truncate">
                      {file.name} ({(file.size / (1024 * 1024)).toFixed(1)}MB)
                    </p>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleUpload}
                  disabled={uploading || !uploaderName.trim()}
                  className="flex-1 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? `Uploading...` : `Upload ${selectedFiles.length} File(s)`}
                </Button>
                <Button
                  onClick={() => setShowUploadModal(false)}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                  disabled={uploading}
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Media Viewer Modal */}
        {viewingMedia && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setViewingMedia(null)}>
            <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <Button
                onClick={() => setViewingMedia(null)}
                className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70"
                size="sm"
              >
                <X className="w-5 h-5" />
              </Button>
              
              <img
                src={getMediaUrl(viewingMedia.id)}
                alt={viewingMedia.originalName}
                className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
              />
              
              <div className="mt-4 flex items-center justify-between bg-slate-800/50 backdrop-blur-sm p-4 rounded-lg">
                <div>
                  <p className="text-white font-semibold">{viewingMedia.uploaderName}</p>
                  <p className="text-sm text-slate-400">
                    {new Date(viewingMedia.uploadedAt).toLocaleDateString()}
                  </p>
                </div>
                <Button
                  onClick={() => handleDownloadMedia(viewingMedia.id, viewingMedia.originalName)}
                  className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaEventView;
